import React, { useState, useEffect } from 'react';
import { Circle, Group, Text, Image } from 'react-konva';
// import { TOOLS } from './DrawingTools'; // Removed: unused import
import useImage from 'use-image';

const Token = ({ 
  id, 
  x, 
  y, 
  radius, 
  fill, 
  name = '',
  image = null,
  isSelected = false,
  visionRadius = 0,
  activeTool,
  onClick,
  onActualDragStart,
  onDragMove,
  onDragEnd,
  isDraggable = false,
  stageScale = 1
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [tokenImage] = useImage(image?.src || '', 'anonymous');
  
  // Add window focus handler for proper token synchronization
  useEffect(() => {
    const handleWindowFocus = () => {
      // When window regains focus, force token to recalculate position
      // This helps prevent misaligned tokens after tab switching
      if (isDragging) {
        // If token was being dragged, stop dragging to prevent stuck state
        setIsDragging(false);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleWindowFocus();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isDragging]);
  
  const handleDragStart = (e) => {
    setIsDragging(true);
    // Always call onClick for drag start, but pass isDragStart flag
    if (onClick) {
      // Create a modified event object to indicate this is a drag start
      const dragEvent = {
        ...e,
        isDragStart: true,
        evt: {
          ...e.evt,
          ctrlKey: e.evt?.ctrlKey || false,
          metaKey: e.evt?.metaKey || false
        }
      };
      onClick(id, dragEvent);
    }
    if (onActualDragStart) {
      onActualDragStart(id);
    }
  };
  
  const handleInternalDragMove = (e) => {
    if (onDragMove) {
      onDragMove(id, e.target.x(), e.target.y());
    }
  };
  
  const handleDragEnd = (e) => {
    if (onDragEnd) {
      onDragEnd(id, e.target.x(), e.target.y());
    }
    // Small delay to prevent accidental clicks right after drag
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };
  
  const [clickTimeout, setClickTimeout] = useState(null);
  
  const handleClick = (e) => {
    // Prevent click during or right after dragging
    if (isDragging) {
      return;
    }
    
    if (clickTimeout) {
      // This is a double click
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      
      if (onClick) {
        onClick(id, { ...e, isDoubleClick: true });
      }
    } else {
      // This might be the first click of a double click
      const timeout = setTimeout(() => {
        setClickTimeout(null);
        if (onClick) {
          onClick(id, e);
        }
      }, 300); // 300ms timeout for double click detection
      
      setClickTimeout(timeout);
    }
  };
  
  const strokeWidth = isSelected ? 3 : 2;
  const strokeColor = isSelected ? '#e74c3c' : '#333';
  
  const fontSize = Math.max(10, 14 / Math.max(stageScale, 0.5));
  
  return (
    <Group 
      x={x} y={y} 
      draggable={isDraggable} 
      onDragStart={handleDragStart} 
      onDragMove={handleInternalDragMove} 
      onDragEnd={handleDragEnd} 
      onClick={handleClick}
      id={id}
      name="token-group"
    >
      {/* Vision radius circle - relative to group origin */}
      {visionRadius > 0 && isSelected && (
        <Circle
          x={0}
          y={0}
          radius={visionRadius}
          stroke="#3498db"
          strokeWidth={1}
          dash={[5, 5]}
          opacity={0.3}
          listening={false}
        />
      )}
      
      {/* Main token circle - relative to group origin */}
      <Circle
        x={0}
        y={0}
        radius={radius}
        fill={fill}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        perfectDrawEnabled={false}
        shadowColor="#000"
        shadowBlur={isDragging ? 10 : 0}
        shadowOpacity={0.3}
        shadowOffset={{ x: 2, y: 2 }}
      />
      
      {/* Token image - displayed over the circle if available */}
      {tokenImage && image && (
        <Image
          x={-radius}
          y={-radius}
          width={radius * 2}
          height={radius * 2}
          image={tokenImage}
          cornerRadius={radius}
          listening={false}
          clipFunc={(ctx) => {
            ctx.arc(0, 0, radius, 0, Math.PI * 2, false);
          }}
        />
      )}
      
      {/* Token name - relative to group origin */}
      {name && (
        <Text
          x={-200}
          y={radius + 5}
          width={400}
          text={name}
          fontSize={fontSize}
          fontStyle="bold"
          fill="#f0f0f0"
          stroke="#000"
          strokeWidth={0.5}
          align="center"
          verticalAlign="top"
          listening={false}
          wrap="none"
        />
      )}
    </Group>
  );
};

export default Token; 